import { useEffect, useRef, useState } from "react";
import { logConnectorFailure } from "../../../telemetry";
import type { Schedule } from "../domain/freeBusy";

export type LoadSchedules = (emails: readonly string[], start: Date, end: Date) => Promise<Map<string, Schedule>>;

const NOBODY: ReadonlyMap<string, Schedule> = new Map();

/**
 * Colleagues' free/busy for one time range, read once per set of people.
 * Null while loading. If Graph can't be read, everyone comes back unknown.
 */
export function useAvailability(
  load: LoadSchedules,
  emails: readonly string[],
  start: Date,
  end: Date,
  enabled = true,
): ReadonlyMap<string, Schedule> | null {
  const [state, setState] = useState<{ key: string; schedules: ReadonlyMap<string, Schedule> } | null>(null);
  const key = enabled && emails.length > 0 ? `${[...emails].sort((a, b) => a.localeCompare(b)).join(",")}|${start.getTime()}|${end.getTime()}` : "";
  const argsRef = useRef({ load, emails, start, end });
  argsRef.current = { load, emails, start, end };

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const args = argsRef.current;
    args
      .load(args.emails, args.start, args.end)
      .then((schedules) => {
        if (!cancelled) setState({ key, schedules });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ key, schedules: new Map() });
        logConnectorFailure("calendar", e, "load-free-busy");
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  if (!key) return NOBODY;
  return state?.key === key ? state.schedules : null;
}
